/* global DetectUserAgent */
import { useState, useMemo, useContext } from "react";

import { getTrackingId } from "tracking/jibjab/tracking";
import { InAppBrowserIntentContext } from "contexts/InAppBrowserIntentContext";

const INSTAGRAM_ANDROID_INTENT_DISABLED_FROM_VERSION = "422.0.0.44.64";

const useInAppBrowserIntent = (browseInputRef, onIntentRedirect) => {
  const intentTriggeredRef = useContext(InAppBrowserIntentContext);
  const [wasBrowsePhotoClicked, setWasBrowsePhotoClicked] = useState(
    intentTriggeredRef ? intentTriggeredRef.current : false,
  );
  const source = DetectUserAgent.isFB4Android() ? "facebook" : "instagram";

  const shouldUseInAppIntentWorkaround = useMemo(() => {
    if (DetectUserAgent.isFB4Android()) return true;
    if (!DetectUserAgent.isInstagram4Android()) return false;
    return DetectUserAgent.isInstagramAppVersionOlderThan(INSTAGRAM_ANDROID_INTENT_DISABLED_FROM_VERSION);
  }, []);

  const intentUrl = useMemo(() => {
    if (!shouldUseInAppIntentWorkaround) {
      return null;
    }

    const trackingId = getTrackingId();
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("landing_default_browser", source);
    currentUrl.searchParams.set("tracking_id", trackingId);

    const url = currentUrl.toString();

    return `${url.replace(/^https?:\/\//, "intent://")}#Intent;scheme=https;S.browser_fallback_url=${encodeURIComponent(
      url,
    )};end;`;
  }, [shouldUseInAppIntentWorkaround]);

  const onFileSelected = () => {
    if (intentTriggeredRef) intentTriggeredRef.current = false;
  };

  const handleInAppBrowserIntent = () => {
    if (!shouldUseInAppIntentWorkaround) return false;

    if (intentTriggeredRef && intentTriggeredRef.current) {
      setWasBrowsePhotoClicked(true);

      if (intentUrl) {
        if (onIntentRedirect) onIntentRedirect(source);
        window.location.assign(intentUrl);
      }

      return true;
    }

    if (intentTriggeredRef) intentTriggeredRef.current = true;

    if (browseInputRef.current) {
      browseInputRef.current.addEventListener("change", onFileSelected, { once: true });
    }

    return false;
  };

  return { handleInAppBrowserIntent, wasBrowsePhotoClicked, intentUrl, shouldUseInAppIntentWorkaround, source };
};

export default useInAppBrowserIntent;
